<template>
  <b-container class="w-100 mt-4">
    <div class="bg-transparent rounded-lg p-3">
      <b-row align-v="center" class="w-100 ml-0">
        <b-col>
          <h5>Upload History</h5>
        </b-col>
      </b-row>
      <b-row align-v="center" class="w-100 ml-0 justify-content-md-end">
        <b-col class="col-md-4">
          <b-row align-v="center" class="show-deleted">
            <b-row class="m-0">
            <b-col class="col-md-9 text-center flex-column">
              <span class="show-deleted-label">Show deleted matches</span>
            </b-col>
            <b-col class="col-md-1 offset-md-1 text-center">
              <b-checkbox
                v-test="'toggle-deleted'"
                :disabled="showLoading"
                id="toggle-deleted"
                size="lg"
                v-model="returnDeleted"
                @change="toggleDeleted"
                switch
              >
              </b-checkbox>
            </b-col>
          </b-row>
            <b-row class="m-0">
              <b-col class="col-md-9 text-center align-self-center">
                <span class="show-deleted-label">Refresh upload history</span>
              </b-col>
              <b-col class="col-md-2 text-center ml-1">
                <b-button class="refresh-btn text-center" variant="light" @click="refreshBatches">
                  <b-icon-arrow-clockwise />
                </b-button>
              </b-col>
            </b-row>
          </b-row>
        </b-col>
      </b-row>
      <div class="mt-3 bg-white card">
        <b-table
          id="batches-table"
          borderless
          striped
          small
          show-empty
          stacked="md"
          :fields="computedFields"
          :items="batchTableProvider"
          primary-key="batchId"
          :current-page="currentPage"
          :per-page="itemsPerPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          table-class="upload-history-table"
          :tbody-tr-class="getRowClass"
          head-variant="light"
        >
          <template #head()="head">
            {{head.label}}
            <SortIcon v-if="head.field.sortable" :sort-desc="sortDesc" :active="sortBy === head.field.key" class="mx-1" />
            <b-icon-question-circle v-if="head.field.tooltip" v-infinity-tooltip="head.field.tooltip" class="text-info ml-1" />
          </template>
          <template #cell(matchedRows)="data">
            <span v-if="data.item.hasFailedState">Error</span>
            <span v-else-if="data.item.isProcessing">Processing</span>
            <span v-else-if="data.item.hasNotAcceptableState">Invalid Upload Data</span>
            <span v-else>{{ data.item.getMatchedRows() }}</span>
          </template>
          <template #cell(totalRows)="data">
            <span v-if="data.item.hasFailedState">Error</span>
            <span v-else-if="data.item.isProcessing">Processing</span>
            <span v-else-if="data.item.hasNotAcceptableState">Invalid Upload Data</span>
            <span v-else>{{ data.item.getTotalRows() }}</span>
          </template>
          <template #cell(matchRate)="data">
            {{ data.item.getMatchRate() }}
          </template>
          <template #cell(isSftpUpload)="data">
            <SftpIcon v-if="data.item.ftpUpload" v-infinity-tooltip="'SFTP upload'"/>
          </template>
          <template #cell(button)="data">
            <span
              :disabled="data.item.isComplete"
              v-infinity-tooltip="getTooltipMessage(data.item)"
            >
              <b-button
                class="dropdown-btn"
                :disabled="!data.item.filename || data.item.isDeleted || !(data.item.isComplete || data.item.hasFailedState || data.item.hasNotAcceptableState)"
                :hidden="!data.item.filename || data.item.isDeleted"
                :id="`batch-${data.item.batchId}`"
                :ref="`batch-${data.item.batchId}`"
                variant="button"
                @click="showDropdownMenu(data.item)"
                v-test="`trigger-${data.item.batchId}`"
              >
                <b-icon-three-dots-vertical />
              </b-button>
            </span>
          </template>
          <template #empty="">
            <span class="py-2">
              <template v-if="!hasError">There are no batches to show. Upload a file to get started with Smart Match.</template>
              <template v-else>We had a problem fetching your batches. Please try again later.</template>
            </span>
          </template>
        </b-table>

        <infinity-popover
          :target="menuTarget"
          :show="showDropdown"
          triggers=""
          customClass="table-popover"
          @hidden="onDropdownHide"
          @clickOutside="onDropdownHide"
          v-test="'popover'"
        >
          <b-list-group v-if="selectedBatch">
            <b-list-group-item v-if="selectedBatch.isComplete"
              @click="viewMatchEntries"
            >
              <div class="row">
                <div class="col-md-1">
                  <b-icon-eye-fill class="icon icon--size-s" />
                </div>
                <div class="col-md-8">
                  View match entries
                </div>
                <div class="col-md-1">
                  <b-icon-arrow-right class="icon icon--size-s" />
                </div>
              </div>
            </b-list-group-item>
            <b-list-group-item v-if="selectedBatch.isComplete"
              :disabled="selectedBatch.isDeleted || !selectedBatch.getFilename()"
              @click="downloadFile"
              v-test="'file download'"
            >
              <div class="row">
                <div class="col-md-1">
                  <b-icon-cloud-download class="icon icon--size-s" />
                </div>
                <div class="col-md-10">
                  Download match data
                </div>
              </div>
            </b-list-group-item>
            <b-list-group-item
              :disabled="!(selectedBatch.isComplete || selectedBatch.isProcessing || selectedBatch.hasFailedState || selectedBatch.hasNotAcceptableState)"
              @click="selectBatchForDelete(selectedBatch)"
            >
              <div class="row">
                <div class="col-md-1">
                  <b-icon-trash-fill class="icon icon--size-s" />
                </div>
                <div class="col-md-10">
                  Delete match data
                </div>
              </div>
            </b-list-group-item>
          </b-list-group>
        </infinity-popover>

        <div class="d-flex w-100 justify-content-center">
          <b-pagination
            class="infinity-pagination"
            size="sm mb-0"
            v-model="currentPage"
            :total-rows="totalItems"
            :per-page="itemsPerPage"
            aria-controls="batches-table"
          />
        </div>

        <b-modal
          id="delete-batch-modal"
          static
          size="lg"
          class="text-left text-whale"
        >
          <template #modal-header>
            <h4>Delete upload</h4>
          </template>
          <p class="modal-text">
            You are about to delete this upload.
          </p>
          <b-table
            bordered
            responsive
            id="delete-batch-table"
            :fields="computedModalFields"
            :items="deleteTableProvider"
            primary-key="batchId"
            table-class="upload-history-table"
            head-variant="light"
          >
            <template #head()="head">
              {{head.label}}
              <SortIcon v-if="head.field.sortable" :sort-desc="sortDesc" :active="sortBy === head.field.key" class="mx-1" />
              <b-icon-question-circle v-if="head.field.tooltip" v-infinity-tooltip="head.field.tooltip" class="text-info ml-1" />
            </template>
            <template #cell(matchedRows)="data">
              {{ data.item.isProcessing ? '-' : data.item.getMatchedRows() }}
            </template>
            <template #cell(totalRows)="data">
              {{ data.item.isProcessing ? '-' : data.item.getTotalRows() }}
            </template>
            <template #cell(matchRate)="data">
              {{ data.item.matchRate ? `${data.item.matchRate}%` : '-' }}
            </template>
          </b-table>
          <div class="modal-bg-tan p-3 mb-3">
            <p>
              By deleting this upload, you will delete the data mapped from the CSV file you uploaded.
            </p>
            <p class="mb-0">
              This data will not be retrievable once deleted.
            </p>
          </div>
          <p class="modal-text mb-0"><strong>
            Are you sure you want to delete this upload?
          </strong></p>
          <template #modal-footer>
            <b-button @click="$bvModal.hide('delete-batch-modal')">
              Cancel
            </b-button>
            <b-button variant="danger" @click="deleteBatches">
              Delete
            </b-button>
          </template>
        </b-modal>
      </div>
    </div>
    <b-toaster name="b-toaster-bottom-right"></b-toaster>
    <div v-if="showLoading" id="infinity-loader-body" v-test="'infinity-loader'">
      <div id="infinity-spinner-body" class="d-flex justify-content-center">
        <b-spinner id="infinity-spinner" small variant="light"></b-spinner>
      </div>
    </div>
  </b-container>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator'
import {
  ButtonPlugin,
  TablePlugin,
  BIconArrowClockwise,
  BIconCloudDownload,
  BIconTrashFill,
  BIconEyeFill,
  BIconArrowRight,
  BIconThreeDotsVertical,
  BIconQuestionCircle,
  BIconCheck,
  BSpinner,
  BDropdownDivider,
  ModalPlugin,
  ListGroupPlugin,
  ToastPlugin,
  PaginationPlugin
} from 'bootstrap-vue'
import Batch from '@/models/Batch'
import { formatDate } from '@infinity/shared/utils/time'
import BatchRepo from '@/repos/BatchRepo'
import { AuthUtil } from '@infinity/shared/utils/auth'
import SortIcon from '@/components/SortIcon.vue'
import SftpIcon from '@/components/SftpIcon.vue'
import BatchResponse from '@/models/BatchResponse'
import { drop, isArray, take } from 'lodash'

Vue.use(ListGroupPlugin)
Vue.use(ModalPlugin)
Vue.use(TablePlugin)
Vue.use(ButtonPlugin)
Vue.use(ToastPlugin)
Vue.use(PaginationPlugin)

export enum SortableFields { MatchDatetime = 'matchDatetime', BatchID = 'batchId', StartDatetime = 'startDatetime', EndDatetime = 'endDatetime', Filename = 'filename' }

@Component({
  components: {
    BSpinner,
    BIconArrowClockwise,
    BIconCloudDownload,
    BIconTrashFill,
    BIconEyeFill,
    BIconArrowRight,
    BIconThreeDotsVertical,
    BIconQuestionCircle,
    BIconCheck,
    BDropdownDivider,
    SortIcon,
    SftpIcon
  }
})
export default class UploadHistory extends Vue {
  @Prop({ type: Boolean, default: false })
  isExternalUser!: boolean

  @Prop({ type: Boolean, default: false })
  canAccessMatchRate!: boolean

  // Table variables
  private showLoading = false
  private hasError = false

  get computedFields () {
    const baseFields = [
      { key: 'startDatetime', label: 'Start Date', sortable: true, formatter: this.formatDate },
      { key: 'endDatetime', label: 'End Date', sortable: true, formatter: this.formatDate },
      { key: 'matchDatetime', label: 'Upload Date', sortable: true, formatter: this.formatDate },
      { key: 'matchedRows', label: 'Matched Rows', sortable: true, tooltip: 'The number of rows that were matched in the uploaded file.' },
      { key: 'isSftpUpload', label: 'SFTP', sortable: false, tooltip: 'SFTP upload' },
      { key: 'button', label: '', sortable: false }
    ]

    // Conditionally add the fields based on isExternalUser
    if (!this.isExternalUser) {
      baseFields.splice(4, 0, { key: 'totalRows', label: 'Total Rows', sortable: true, tooltip: 'The total number of rows in the uploaded file.' })
      baseFields.splice(5, 0, { key: 'matchRate', label: 'Match Rate', sortable: false, tooltip: 'The percentage of Smart Match events that were successfully matched to calls.' })
    }

    if (this.isExternalUser && this.canAccessMatchRate) {
      baseFields.splice(4, 0, { key: 'matchRate', label: 'Match Rate', sortable: false, tooltip: 'The percentage of Smart Match events that were successfully matched to calls.' })
    }
    return baseFields
  }

  get computedModalFields () {
    const baseModalFields = [
      { key: 'startDatetime', label: 'Start Date', sortable: true, formatter: this.formatDate },
      { key: 'endDatetime', label: 'End Date', sortable: true, formatter: this.formatDate },
      { key: 'matchDatetime', label: 'Upload Date', sortable: true, formatter: this.formatDate },
      { key: 'matchedRows', label: 'Matched Rows', sortable: true, tooltip: 'The number of rows that were matched in the uploaded file.' }
    ]

    // Conditionally add the fields based on isExternalUser
    if (!this.isExternalUser) {
      baseModalFields.splice(4, 0, { key: 'totalRows', label: 'Total Rows', sortable: true, tooltip: 'The total number of rows in the uploaded file.' })
      baseModalFields.splice(5, 0, { key: 'matchRate', label: 'Match Rate', sortable: false, tooltip: 'The percentage of Smart Match events that were successfully matched to calls.' })
    }

    return baseModalFields
  }

  private sortBy = SortableFields.MatchDatetime
  private sortDesc = true

  private returnDeleted = false

  private currentPage = 1
  private itemsPerPage = 20
  private totalItems = 0

  private showDropdown = false
  private selectedBatch: Batch | null = null
  private selectedBatchForDelete: Batch | null = null

  async batchTableProvider () {
    this.showLoading = true
    this.hasError = false

    let items: Batch[] = []
    const batchRepo = new BatchRepo()

    const offset = (this.currentPage - 1) * this.itemsPerPage

    try {
      const response = await batchRepo.getBatches(this.$store.state.installationId, this.returnDeleted, this.itemsPerPage, offset, this.sortBy, this.sortDesc ? 'DESC' : 'ASC')

      if (response instanceof BatchResponse) {
        this.totalItems = response.getTotalBatchCount()
        items = response.getBatches()
      }

      if (isArray(response)) {
        this.totalItems = response.length
        items = take(drop(response, offset), this.itemsPerPage)
      }
    } catch (e) {
      this.hasError = true
    }

    this.showLoading = false

    return items
  }

  deleteTableProvider (): Array<Batch> {
    if (this.selectedBatchForDelete) {
      return [this.selectedBatchForDelete]
    }

    return []
  }

  formatDate (date: string) {
    const timezone = this.$infinityAuth.getInstallation().getTimeZone() !== '' ? this.$infinityAuth.getInstallation().getTimeZone() : 'UTC'
    return formatDate(new Date(date), timezone)
  }

  getRowClass (item: Batch, type: string): string | null {
    if (item && type === 'row') {
      if (item.isDeleted) {
        return 'deleted'
      } else if (item.isReadyToDelete) {
        return 'ready-to-delete'
      }
    }

    return null
  }

  getTooltipMessage (item: Batch): string {
    if (item.hasFailedState) {
      return 'The current batch received an error is not processing.'
    } else if (item.hasNotAcceptableState) {
      return 'The current batch contains invalid data.'
    } else if (item.isProcessing) {
      return 'The current batch is still processing.'
    } else if (item.isReadyToDelete) {
      return 'The current batch is marked for deletion.'
    }

    return ''
  }

  toggleDeleted () {
    this.$root.$emit('bv::refresh::table', 'batches-table')
    this.toggleToaster()
  }

  refreshBatches () {
    this.$root.$emit('bv::refresh::table', 'batches-table')
  }

  toggleToaster () {
    this.$bvToast.hide('infinity-toaster')
    setTimeout(() => {
      const h = this.$createElement
      const vNodesTitle = h(
        'div',
        { class: ['d-flex', 'flex-grow-1', 'align-items-center', 'mr-2'] },
        [
          h('b-icon-check', { class: 'mr-2 icon--size-m' }),
          h('strong', { class: 'mr-2' }, `${this.returnDeleted ? 'Show' : 'Hide'} deleted matches`)
        ])
      this.$bvToast.toast(`Deleted matches will now be ${this.returnDeleted ? 'shown' : 'hidden'} in the table.`, {
        title: [vNodesTitle],
        id: 'infinity-toaster',
        toaster: 'b-toaster-bottom-right',
        toastClass: 'infinity-toaster',
        headerClass: 'infinity-toaster-header',
        bodyClass: 'infinity-toaster-body',
        autoHideDelay: 5000
      })
    }, 500)
  }

  async downloadFile () {
    if (this.selectedBatch) {
      try {
        await this.$store.dispatch('getBatchFile', { filename: this.selectedBatch.getFilename() })

        location.href = this.$store.state.batchFile.getUrl()
      } catch (e) {
        this.$bvToast.toast('We encountered a problem downloading this file. Please try again.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        })
      }

      await this.$mixpanelService.track('settings.smartMatch.uploadHistory.downloadFile', {
        filename: this.selectedBatch.getFilename()
      })
    }

    this.onDropdownHide()
  }

  async deleteBatches () {
    if (this.selectedBatchForDelete) {
      const batchesRepo = new BatchRepo()
      try {
        await batchesRepo.deleteBatches(this.$store.state.installationId, this.selectedBatchForDelete.getBatchId())
      } catch (e) {
        this.$bvToast.toast('We encountered a problem deleting this batch.', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000
        })
      }

      await this.$mixpanelService.track('settings.smartMatch.uploadHistory.deleteBatch', {
        batchId: this.selectedBatchForDelete.getBatchId()
      })

      this.$root.$emit('bv::refresh::table', 'batches-table')
      this.$bvModal.hide('delete-batch-modal')
      this.selectedBatchForDelete = null
    }
  }

  viewMatchEntries () {
    if (this.selectedBatch) {
      const host = window.location.host
      const dateRange = this.getDateRange(this.selectedBatch.getStartDatetime(), this.selectedBatch.getEndDatetime())
      window.location.href = window.location.protocol + '//' +
        host + `/d/a/log/smart-match?crm=eqi~${this.selectedBatch.getBatchId()}~s&${dateRange}&igrp=${AuthUtil.installationId}`
    }

    this.onDropdownHide()
  }

  getDateRange (startDate: string, endDate: string) {
    const start = new Date(startDate)
    start.setDate(start.getDate() - 1)
    const end = new Date(endDate)
    end.setDate(end.getDate() + 1)

    return `dr=${start.toISOString().slice(0, 10)}` +
            `~${end.toISOString().slice(0, 10)}~c`
  }

  get menuTarget (): Vue | null {
    if (this.selectedBatch) {
      return this.$refs[`batch-${this.selectedBatch.getBatchId()}`] as Vue
    }

    return null
  }

  onDropdownHide () {
    this.$nextTick(() => {
      this.showDropdown = false
      this.selectedBatch = null
    })
  }

  onClickOutside () {
    this.onDropdownHide()
  }

  showDropdownMenu (batch: Batch) {
    if (batch === this.selectedBatch) {
      this.onClickOutside()
      return
    }

    this.showDropdown = false
    this.selectedBatch = batch
    this.showDropdown = true
  }

  selectBatchForDelete (batch: Batch) {
    this.selectedBatchForDelete = batch
    this.$root.$emit('bv::refresh::table', 'delete-batch-table')
    this.onDropdownHide()
    this.$bvModal.show('delete-batch-modal')
  }
}

</script>
<style lang="scss">
.refresh-btn {
  height: 1.8rem;
  width: 2.8rem;
  margin-top: 8px;
  padding:0;
  background-color: #adb5bd;
  border-color: #adb5bd;
}
button.close {
  color: white;
  font-size: 2rem;
  opacity: unset;
}
.icon--size-m {
  width: 2.75em;
  height: 2.75em;
}
.infinity-toaster {
  background-color: rgb(76, 175, 80) !important;
  border-radius: 3px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.5);
  min-width: 45%;
  max-width: 100%;
  min-height:80px;
  font-size: 15px;
}
.infinity-toaster-header {
  color: rgb(255, 255, 255);
  border: none;
  background-color: transparent;
  font-family: OpenSans-Regular, sans-serif;
  font-weight: normal;
  letter-spacing: 1px;
  line-height: 19px;
}
.infinity-toaster-body {
  margin-left: 48px;
  color: rgb(255, 255, 255);
  background-color: transparent;
  font-family: OpenSans-Regular, sans-serif;
  font-weight: normal;
  letter-spacing: 1px;
  line-height: 19px;
  padding-top: 0;
}
.b-toast {
  max-width: 100%;
}

.b-toaster.b-toaster-bottom-right .b-toaster-slot {
  max-width: 45%;
}
#infinity-loader-body {
  z-index: 1000;
  padding: 0;
  background: transparent;
  position: fixed;
  height: 100%;
  top: 0;
  left: 230px;
  right: 0;
}
#infinity-spinner-body {
  z-index: 10000;
  top: 50%;
  background: rgba(0, 0, 0, 0.7);
  width: 50px;
  height: 50px;
  border-radius: 5px;
  left: 48.6%;
  position: absolute;
  #infinity-spinner {
    margin: 1.3rem;
  }
}
.custom-control-input:checked ~ .custom-control-label::before {
  color: #fff;
  border-color: #2196f3 !important;
  background-color: #2196f3;
}
.custom-switch.b-custom-control-lg .custom-control-label::before, .input-group-lg .custom-switch .custom-control-label::before {
  top: 0;
  height: 1.75rem;
  left: -4rem;
  width: 3.1875rem;
  border-radius: 3px;
  background: transparent;
  border-color: #adb5bd;
}
.custom-switch.b-custom-control-lg .custom-control-label::after, .input-group-lg .custom-switch .custom-control-label::after {
  top: 0;
  height: 1.75rem;
  right: 0;
  width: 1.7875rem;
  border-radius: 3px;
  background-size: 50% 50%;
}
.custom-switch .custom-control-input:checked ~ .custom-control-label::after {
  top: 0;
  border-radius: 3px;
  height: 1.75rem;
  right: 0;
  width: 1.7875rem;
  background-color: #3498db;
  transform: translateX(0);
}
.custom-switch.b-custom-control-lg .custom-control-input:checked ~ .custom-control-label::after, .input-group-lg .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
  transform: translateX(-1.375rem)
}
.show-deleted {
  padding: 5px;
  height: 5rem;
  background: rgb(255, 255, 255);
  border-radius: 3px;
  color: rgb(38, 52, 62);
  font-size: 1em;
  font-weight: 600;
  line-height: 17.4px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
}
.show-deleted.input {
  height: 1rem;
}
.show-deleted-label {
  align-self: center;
  font-size: 1em;
}
.card {
  padding: 1rem;
  background: rgb(255, 255, 255);
  border: none;
  border-radius: 3px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
}
.table {
  border-radius: 3px;
}
.table td {
  vertical-align: unset;
}
table td > span, table th > span {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}
table td > span span, table th > span span {
  padding-right: 5px;
}
.table-striped tbody tr:nth-of-type(odd) {
  background-color: transparent;
}
.table-striped > tbody > tr:nth-child(2n) > td, .table-striped > tbody > tr:nth-child(2n) > th {
  background-color: rgba(0, 0, 0, 0.05);
}
.checkbox-border {
  padding: 0.375rem 0.75rem 0.375rem 3rem;
  border: 1px solid #6c757d;
  border-radius: 0.25rem;
}
.upload-history-table {
  text-align: center;
  thead {
    tr {
      border-radius: $border-radius;

      th {
        background-image: none !important;

        &:first-child {
          border-top-left-radius: $border-radius;
          border-bottom-left-radius: $border-radius;
        }

        &:last-child {
          border-top-right-radius: $border-radius;
          border-bottom-right-radius: $border-radius;
        }
      }
    }
  }
}
.ready-to-delete {
  text-decoration: line-through;
}
.deleted {
  text-decoration: line-through;
  pointer-events: none;
}
.modal-text {
  font-size: 1.2rem;
}
.modal-bg-tan {
  background-color: #ffedb0;
}
.no-gutters {
  margin-right: 0;
  margin-left: 0;
}
.icon--size-s {
  width: 1.25em;
  height: 1.25em;
}
.icon {
  flex-shrink: 0;
}
.table-popover {
  min-width: 251px;
  max-width: inherit;
  .arrow {
    margin: 0;
  }

  .arrow::after {
    border-bottom-color: black;
    border-top-color: black;
  }

  .popover-body {
    padding: 0;
    background: #1c1c1b;
    font-size: 14px;

    .list-group {
      .list-group-item + * {
        border-top: 1px solid #4a4a49 !important;
      }
      .list-group-item {
        cursor: pointer;
        color: #fff;
        border-top: 0;
        border-radius: 0;
        background: transparent;
        padding: 10px 20px 10px 10px;
      }
      .list-group-item:hover {
        background-color: rgba(125, 127, 129, 0.5);
      }
    }
  }
}

.infinity-pagination {
  .page-item {
    span, button {
      border: none;
      border-radius: $border-radius;
      margin: 0 0.25rem
    }
  }
}
</style>
